import React, { forwardRef } from 'react';
import {
  Button,
  GDSButtonIndicator,
  GDSButtonProps,
  GDSButtonSize,
  GDSButtonWidth,
} from '#components/button/button.tsx';
import {
  GDSTextActionIndicator,
  GDSTextActionProps,
  TextAction,
} from '#components/text-action/text-action.tsx';
import { StackLayout } from '#components/stack-layout/stack-layout.tsx';
import {
  GDSHorizontalStackLayoutProps,
  GDSVerticalStackLayoutProps,
} from '#components/stack-layout/stack-layout.types.ts';
import {
  GDSCustomizableComponent,
  PolymorphicComponentPropWithRef,
} from '../../types';
import { getLinkOrButtonProps } from '../utils/get-link-or-button-props';

type GDSCtaType = 'primary' | 'secondary' | 'tertiaryQuiet' | 'textAction';

type GDSCtaIndicator = GDSButtonIndicator | GDSTextActionIndicator;

type GDSBaseCtaProps<C extends React.ElementType> =
  PolymorphicComponentPropWithRef<
    C,
    {
      as?: 'a' | 'button' | React.ElementType;
      href?: string;
      icon?: string;
      iconAltText?: string;
      indicator?: GDSCtaIndicator;
      indicatorAltText?: string;
      label?: string;
      onClick?: (event: React.MouseEvent) => void;
      size?: GDSButtonSize;
      width?: GDSButtonWidth;
    }
  >;

type GDSTextActionCtaProps<C extends React.ElementType = any> = Omit<
  GDSBaseCtaProps<C>,
  'size'
> & {
  type: 'textAction';
};

type GDSFirstCTAProps<C extends React.ElementType = any> =
  | GDSTextActionCtaProps<C>
  | (GDSBaseCtaProps<C> & {
      type?: Exclude<GDSCtaType, 'primary' | 'textAction'>;
    });

type GDSSecondCTAProps<C extends React.ElementType = any> =
  | GDSTextActionCtaProps<C>
  | (GDSBaseCtaProps<C> & {
      type?: Exclude<GDSCtaType, 'primary' | 'textAction'>;
    });

type GDSCtaPosition = 'leading' | 'fullWidth' | 'centered';

export type GDSComposableCardCtaSectionProps = GDSCustomizableComponent & {
  firstCTA: GDSFirstCTAProps;
  secondCTA?: GDSSecondCTAProps;
  position?: GDSCtaPosition;
};

const CTA = <C extends React.ElementType>(
  ctaProps: GDSBaseCtaProps<C> & { type: GDSCtaType; position: GDSCtaPosition },
) => {
  const {
    as,
    href,
    icon,
    iconAltText,
    indicator,
    indicatorAltText,
    label,
    onClick,
    position,
    size = 'medium',
    type,
    ...extraProps
  } = ctaProps || {};

  const sharedProps = {
    icon,
    iconLabel: iconAltText || '',
    indicator,
    indicatorLabel: indicatorAltText,
    ...extraProps,
    ...getLinkOrButtonProps(onClick, href, as),
  };

  if (type === 'textAction') {
    return (
      <TextAction
        {...(sharedProps as Partial<GDSTextActionProps<'a'>>)}
        css={{
          alignSelf: position === 'fullWidth' ? 'center' : undefined,
          textAlign:
            position === 'fullWidth' || position === 'centered'
              ? 'center'
              : undefined,
        }}
      >
        {label}
      </TextAction>
    );
  }

  return (
    <Button
      priority={type === 'tertiaryQuiet' ? 'tertiary' : type}
      quiet={type === 'tertiaryQuiet'}
      size={size}
      width={position === 'fullWidth' ? 'fillContainer' : undefined}
      textAlign={
        position === 'fullWidth' || position === 'centered'
          ? 'center'
          : undefined
      }
      {...(sharedProps as Partial<GDSButtonProps<'a'>>)}
    >
      {label}
    </Button>
  );
};

const getOrientationAndAlignment = (ctaPosition: GDSCtaPosition) => {
  switch (ctaPosition) {
    case 'fullWidth':
      return {
        orientation: 'vertical',
        horizontalAlignment: 'stretch',
      } as GDSVerticalStackLayoutProps;
    case 'centered':
      return {
        orientation: 'horizontal',
        horizontalAlignment: 'center',
        verticalAlignment: 'center',
      } as GDSHorizontalStackLayoutProps;
    case 'leading':
    default:
      return {
        orientation: 'vertical',
        horizontalAlignment: 'start',
      } as GDSHorizontalStackLayoutProps;
  }
};

export const ComposableCardCTASection = forwardRef<
  HTMLDivElement,
  GDSComposableCardCtaSectionProps
>(({ className, css, firstCTA, secondCTA, position = 'leading' }, ref) => (
  <StackLayout
    spacing="$half"
    ref={ref}
    className={['GDS-composable-card-cta-section', className].join(' ')}
    css={{ flexWrap: 'wrap', marginTop: 'auto', flex: 0, ...css }}
    {...getOrientationAndAlignment(position)}
  >
    {firstCTA && (
      <CTA
        type={firstCTA?.type || 'primary'}
        position={position}
        {...firstCTA}
      />
    )}

    {firstCTA && secondCTA && (
      <CTA
        type={secondCTA?.type || 'textAction'}
        position={position}
        {...secondCTA}
      />
    )}
  </StackLayout>
));
